/**
 * Logseq-outline → CommonMark normalizer (paste compatibility).
 *
 * Logseq exports an OUTLINE, not standard markdown: every block is a line, nesting is by TAB
 * (one tab per level), the page's first block often omits its `- ` marker, block metadata rides
 * on `key:: value` property lines, and a single block can span several lines (e.g. a multi-line
 * HTML table). markdown-it reads those tabs as indented code blocks and the bare heading as a
 * detached block, so a raw paste mis-nests. This rewrites the outline into clean nested markdown
 * (consistent 2-space indents, every block a `- ` item, properties dropped) that markdown-it —
 * and therefore the Lapo parser — handles correctly.
 *
 * It is a no-op for content that doesn't look like a Logseq outline (no tabs, no properties), so
 * ordinary markdown pastes pass straight through untouched.
 */

interface OutlineBlock {
  depth: number;
  lines: string[];
  collapsed: boolean;
}

/** Result of normalizing: clean markdown, plus per-block collapse flags in document (pre)order. */
export interface NormalizedLogseq {
  markdown: string;
  /** `collapsed[i]` is true when the i-th emitted block (pre-order = list-item order) was folded. */
  collapsed: boolean[];
}

const PROPERTY_RE = /^[ \t]*[A-Za-z][A-Za-z0-9_-]*::(\s|$)/;
const COLLAPSED_RE = /^[ \t]*collapsed::\s*true\s*$/i;
const HEADING_RE = /^#{1,6}\s/;

/** Heuristic: tab-indentation or `key:: value` properties mark Logseq-flavored markdown. */
export function looksLikeLogseqOutline(markdown: string): boolean {
  return /\n[ ]*\t/.test('\n' + markdown) || /^[ \t]*[A-Za-z][A-Za-z0-9_-]*::(\s|$)/m.test(markdown);
}

function leadingTabs(line: string): number {
  let n = 0;
  while (line[n] === '\t') n += 1;
  return n;
}

export function normalizeLogseqOutline(markdown: string): NormalizedLogseq {
  if (!looksLikeLogseqOutline(markdown)) return { markdown, collapsed: [] };

  const blocks: OutlineBlock[] = [];
  for (const raw of markdown.split('\n')) {
    if (raw.trim() === '') {
      // Blank line: keep it inside the current block (multi-line HTML/text), else ignore.
      if (blocks.length) blocks[blocks.length - 1]!.lines.push('');
      continue;
    }
    if (PROPERTY_RE.test(raw)) {
      // Properties are metadata, not content — drop them, but honor `collapsed:: true` by folding
      // the block it belongs to (the most recent one).
      if (COLLAPSED_RE.test(raw) && blocks.length) blocks[blocks.length - 1]!.collapsed = true;
      continue;
    }

    const depth = leadingTabs(raw);
    const rest = raw.slice(depth);
    const bullet = rest.startsWith('- ') ? rest.slice(2) : rest === '-' ? '' : null;

    if (bullet !== null) {
      blocks.push({ depth, lines: [bullet], collapsed: false });
    } else if (blocks.length === 0 || HEADING_RE.test(rest.trimStart())) {
      // Bare block — the page's first block, or a heading Logseq left un-bulleted.
      blocks.push({ depth, lines: [rest.trimStart()], collapsed: false });
    } else {
      // Continuation of the current block (e.g. the inner lines of a multi-line HTML table).
      blocks[blocks.length - 1]!.lines.push(rest.replace(/^ {0,2}/, ''));
    }
  }

  const out: string[] = [];
  for (const block of blocks) {
    const indent = '  '.repeat(block.depth);
    out.push(`${indent}- ${block.lines[0] ?? ''}`);
    for (const cont of block.lines.slice(1)) {
      out.push(cont === '' ? '' : `${indent}  ${cont}`);
    }
  }
  return { markdown: out.join('\n'), collapsed: blocks.map((b) => b.collapsed) };
}
